import { Component, OnDestroy, OnInit } from "@angular/core";
import {
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "src/app/shared/services/authentication/auth.service";
import { PlaysService } from "src/app/shared/services/plays/plays.service";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { HttpClient, HttpEventType } from "@angular/common/http";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { environment } from "src/environments/environment";
@Component({
  selector: "app-upload",
  templateUrl: "./upload.component.html",
  styleUrls: ["./upload.component.scss"],
})
export class UploadComponent implements OnInit, OnDestroy {
  uploadProgress: number = 0;
  isUploadingVideo = false;
  isUploadingAlbum = false;
  imagePreview: string | ArrayBuffer = "";
  thumbnailPreview: string | ArrayBuffer = "";
  albumForm!: UntypedFormGroup;
  releaseForm: FormGroup;
  tracksFiles: Blob;
  trackIndex: number = 0;
  selection: "music" | "video" | null = null; // To manage UI state
  canUploadMusic = false;
  canUploadVideo = false;
  showUpgradePrompt = false;
  isLoading = true; // Start with loading true
  isLabelAccount = false;
  showing: boolean = true;
  picBlob: any;
  trackForm: FormGroup;
  songs: any[] = [];
  showTrackForm = false;
  releaseNameSet = false;
  progress: number = 0;
  editMode: boolean = false;
  uploadVidForm: FormGroup;
  albumId: string;
  currentUser: any;
  showArtistSearch = false;
  artistSearchResults: any[] = [];
  artistSearchQuery = new UntypedFormControl("");
  creatorId: string;
  private destroy$ = new Subject<void>();
  uploadLimit: number | "unlimited" = 0;
  currentUploads = 0;

  constructor(
    private router: Router,
    private fb: UntypedFormBuilder,
    private auth: AuthService,
    private play: PlaysService,
    private route: ActivatedRoute,
    private http: HttpClient
  ) {
    this.albumForm = this.fb.group({
      name: ["", Validators.required],
      artist: ["", Validators.required],
      type: ["", Validators.required],
      art: ["", Validators.required],
      released: ["", Validators.required],
      uri: new UntypedFormControl(""),
      appleSave: new UntypedFormControl(""),
      tracks: this.fb.array([]),
      showing: [this.showing, Validators.required],
      price: [0],
      membersOnly: [false],
      tier: [""],
      level: [""],
    });

    this.releaseForm = this.fb.group({
      releaseName: ["", Validators.required],
      releaseArtist: ["", Validators.required],
    });

    this.trackForm = this.fb.group({
      id: [null],
      title: [""],
      artists: this.fb.array([]),
      url: [""],
      uri: [""],
      apple: [""],
      lyrics: [""],
      producers: [""],
      engineers: [""],
      writers: [""],
      comments: [],
      likes: [],
      duration: [""],
      price: [0],
      membersOnly: [false],
      tier: [""],
      level: [""],
    });
  }

  get trackArtists(): UntypedFormArray {
    return this.trackForm.get("artists") as UntypedFormArray;
  }

  get showCreateNewArtistOption(): boolean {
    const query = this.artistSearchQuery.value;
    if (!query || query.length <= 1) {
      return false;
    }
    const queryLower = query.toLowerCase();
    const exactMatchFound = this.artistSearchResults.some(
      (a) => a.name.toLowerCase() === queryLower
    );
    return !exactMatchFound;
  }

  getArtistNames(artists: any[]): string {
    if (!artists || !Array.isArray(artists)) {
      return "";
    }
    return artists.map((artist) => artist.name).join(", ");
  }

  setReleaseName() {
    this.albumForm.patchValue({
      name: this.releaseForm.value.releaseName,
    });
    // For non-label, artist is already set and disabled in ngOnInit.
    // For label, we let them select from the main form.
    this.releaseNameSet = true; // Show the album form after submission
  }

  toggleTrackForm() {
    if (this.showTrackForm === false) {
      this.trackForm.reset();
      const artistsArray = this.trackForm.get("artists") as UntypedFormArray;
      artistsArray.clear();

      // Determine the primary artist for the new track
      let primaryArtist: { id: string; name: string };

      if (this.isLabelAccount) {
        const selectedAlbumArtistName = this.albumForm.get("artist").value;
        // Try to find the selected album artist in the label's list
        primaryArtist = this.currentUser.labelArtists?.find(
          (a) => a.name === selectedAlbumArtistName
        );
        // If not found (or not selected), default to the first artist in the list
        if (!primaryArtist && this.currentUser.labelArtists?.length > 0) {
          primaryArtist = this.currentUser.labelArtists[0];
        }
      } else {
        // For non-labels, the artist is always the current user
        primaryArtist = {
          id: this.currentUser.id,
          name: this.albumForm.get("artist").value, // This is pre-filled with displayName
        };
      }

      if (primaryArtist) {
        artistsArray.push(this.fb.group(primaryArtist));
      }
    }
    this.showTrackForm = !this.showTrackForm;
  }

  private getTodaysDateString(): string {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  ngOnInit(): void {
    this.uploadVidForm = this.fb.group({
      video: [null, Validators.required],
      thumbnail: [null],
      title: ["", Validators.required],
      description: [""],
      youtubeLink: [""],
      duration: [null],
      releaseDate: [this.getTodaysDateString(), Validators.required],
      dspReleaseDate: [this.getTodaysDateString()],
      isDspDateSame: [true],
      type: ["", Validators.required],
      showing: [true],
      price: [0],
      membersOnly: [false],
      tier: [""],
      level: [""],
    });

    this.auth.ui$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (user) {
        this.currentUser = user;
        this.isLabelAccount = user.creatorType === "label";

        // Add the logic for DSP date checkbox
        const dspReleaseDateControl = this.uploadVidForm.get("dspReleaseDate");
        const releaseDateControl = this.uploadVidForm.get("releaseDate");

        this.uploadVidForm
          .get("isDspDateSame")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((isSame) => {
            if (isSame) {
              dspReleaseDateControl.setValue(releaseDateControl.value);
              dspReleaseDateControl.disable();
            } else {
              dspReleaseDateControl.enable();
            }
          });

        releaseDateControl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((newDate) => {
            if (this.uploadVidForm.get("isDspDateSame").value) {
              dspReleaseDateControl.setValue(newDate);
            }
          });

        this.uploadVidForm
          .get("membersOnly")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((isMembersOnly) => {
            if (!isMembersOnly) {
              this.uploadVidForm.patchValue(
                { tier: "", level: "" },
                { emitEvent: false }
              );
            }
          });

        this.uploadVidForm
          .get("tier")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((selectedTier) => {
            const member = this.currentUser?.memberships?.find(
              (m) => m.tier === selectedTier
            );
            this.uploadVidForm.patchValue(
              { level: member?.level || "" },
              { emitEvent: false }
            );
          });

        // Add listeners for albumForm
        this.albumForm
          .get("membersOnly")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((isMembersOnly) => {
            if (!isMembersOnly) {
              this.albumForm.patchValue(
                { tier: "", level: "" },
                { emitEvent: false }
              );
            }
          });

        this.albumForm
          .get("tier")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((selectedTier) => {
            const member = this.currentUser?.memberships?.find(
              (m) => m.tier === selectedTier
            );
            this.albumForm.patchValue(
              { level: member?.level || "" },
              { emitEvent: false }
            );
          });

        // Add listeners for trackForm
        this.trackForm
          .get("membersOnly")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((isMembersOnly) => {
            if (!isMembersOnly) {
              this.trackForm.patchValue(
                { tier: "", level: "" },
                { emitEvent: false }
              );
            }
          });

        this.trackForm
          .get("tier")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((selectedTier) => {
            const member = this.currentUser?.memberships?.find(
              (m) => m.tier === selectedTier
            );
            this.trackForm.patchValue(
              { level: member?.level || "" },
              { emitEvent: false }
            );
          });

        // Initial state
        dspReleaseDateControl.disable();
        this.creatorId = user.id;
        console.log("Current user in upload:", user);

        const routeAlbumId = this.route.snapshot.params["albumId"];

        if (routeAlbumId && routeAlbumId !== "new") {
          // EDIT MODE
          this.albumId = routeAlbumId;
          this.editMode = true;
          this.releaseNameSet = true;
          this.selection = "music"; // Go directly to music form
          this.isLoading = false;

          if (!this.isLabelAccount) {
            this.albumForm.get("artist").disable();
          }

          this.play.getAlbumById(this.albumId).subscribe((albumData) => {
            if (albumData) {
              this.albumForm.patchValue(albumData);
              this.imagePreview = albumData.art;
            }
          });

          this.play.getTracksForAlbum(this.albumId).subscribe((tracks) => {
            this.songs = tracks;
          });
        } else {
          // NEW UPLOAD MODE
          this.albumId = this.play.generateId("album");
          this.editMode = false;
          if (!this.isLabelAccount) {
            this.releaseForm.patchValue({ releaseArtist: user.displayName });
            this.releaseForm.get("releaseArtist").disable();
            this.albumForm.patchValue({ artist: user.displayName });
            this.albumForm.get("artist").disable();
          }
          this.albumForm.patchValue({ released: this.getTodaysDateString() });

          this.checkUploadLimits(user);
        }
      }
    });
  }

  async checkUploadLimits(user: any) {
    this.isLoading = true;
    const creatorType = user?.creatorType;

    if (!creatorType) {
      this.isLoading = false;
      return;
    }

    // Define limits based on creator type
    const limits = {
      creator: 7,
      verified: 20,
      premium: "unlimited",
      label: "unlimited",
    };

    this.uploadLimit = limits[creatorType] || 0;

    if (this.uploadLimit === "unlimited") {
      this.canUploadMusic = true;
      this.canUploadVideo = true;
      this.isLoading = false;
      return;
    }

    try {
      const [discography, videos] = await Promise.all([
        this.play.getAllDiscography(),
        this.play.getVideosByCreator(user.id),
      ]);

      this.currentUploads = (discography?.length || 0) + (videos?.length || 0);

      if (this.currentUploads < this.uploadLimit) {
        this.canUploadMusic = true;
        this.canUploadVideo = true;
      } else {
        this.canUploadMusic = false;
        this.canUploadVideo = false;
        this.showUpgradePrompt = true; // Automatically show prompt if they are at limit
      }
    } catch (error) {
      console.error("Error checking upload limits:", error);
      // Fail open in case of an error fetching counts
      this.canUploadVideo = true;
    } finally {
      this.isLoading = false;
    }
  }

  selectUpload(type: "music" | "video") {
    if (type === "music" && !this.canUploadMusic) {
      this.showUpgradePrompt = true;
    } else if (type === "video" && !this.canUploadVideo) {
      this.showUpgradePrompt = true;
    } else {
      this.selection = type;
    }
  }

  async saveTrack() {
    if (!this.tracksFiles) {
      console.error("No track file selected.");
      return;
    }

    const isEditingTrack = !!this.trackForm.value.id;
    const trackId =
      this.trackForm.value.id || this.play.generateId("track", this.albumId);

    const trackIndex = isEditingTrack ? this.trackIndex : this.songs.length;

    const trackData = { ...this.trackForm.value, id: trackId, trackIndex };
    trackData.price = parseFloat(trackData.price) || 0;
    if (!trackData.membersOnly) {
      trackData.tier = "";
      trackData.level = "";
    }

    console.log("Saving track data:", trackData);

    if (isEditingTrack) {
      const { url, ...metaData } = trackData;
      await this.play.updateTrackInAlbum(this.albumId, trackId, metaData);
    } else {
      await this.play.addTrackToAlbum(this.albumId, trackId, trackData);
    }

    const formData = new FormData();
    formData.append("file", this.tracksFiles);
    formData.append("uploadType", "track");
    formData.append("creatorId", this.creatorId);
    formData.append("albumId", this.albumId);
    formData.append("trackId", trackId);

    this.http
      .post(`${environment.myBackend}/upload`, formData, {
        reportProgress: true,
        observe: "events",
      })
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.progress = Math.round((event.loaded / event.total) * 100);
          } else if (event.type === HttpEventType.Response) {
            const res: any = event.body;
            this.play.updateTrackInAlbum(this.albumId, trackId, {
              url: res.url,
            });

            trackData.url = res.url;
            if (isEditingTrack) {
              this.songs[this.trackIndex] = trackData;
            } else {
              this.songs.push(trackData);
            }
            this.trackForm.reset();
            this.showTrackForm = false;
            this.progress = 0;
          }
        },
        error: (err) => console.error("Error uploading track:", err),
      });
  }

  removeTrack(index: number) {
    this.songs.splice(index, 1);
  }

  removeTrackArtist(index: number): void {
    this.trackArtists.removeAt(index);
  }

  updatePrimaryTrackArtist(artistId: string) {
    const selectedArtist = this.currentUser.labelArtists.find(
      (a) => a.id === artistId
    );
    if (selectedArtist) {
      // Get the first form group in the artists array
      const primaryArtistGroup = this.trackArtists.at(0) as FormGroup;
      // Update its value
      primaryArtistGroup.patchValue({
        id: selectedArtist.id,
        name: selectedArtist.name,
      });
    }
  }

  addTrackArtist(artist: {
    id: string;
    name: string;
    avatarUrl?: string;
  }): void {
    // Prevent adding duplicates
    const exists = this.trackArtists.value.some((a) => a.id === artist.id);
    if (!exists) {
      this.trackArtists.push(
        this.fb.group({ id: artist.id, name: artist.name })
      );
    }
    this.artistSearchResults = [];
    this.artistSearchQuery.reset();
    this.showArtistSearch = false;
  }

  async createNewArtist(name: string) {
    if (!name || name.trim().length === 0) return;
    try {
      const newArtist = await this.play.createStubUser(name.trim());
      this.addTrackArtist(newArtist);
    } catch (error) {
      console.error("Error creating new artist:", error);
    }
  }

  async searchArtists() {
    const query = this.artistSearchQuery.value;
    if (query && query.length > 1) {
      const results = await this.play.searchCreators(query);
      this.artistSearchResults = results.map((user) => ({
        id: user.id,
        name: user.displayName,
        avatarUrl: user.avatarUrl,
      }));
    } else {
      this.artistSearchResults = [];
    }
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.songs, event.previousIndex, event.currentIndex);
    // After reordering locally, update the trackIndex for each song
    this.songs.forEach((song, index) => {
      song.trackIndex = index;
    });

    // Now, save the new order to Firestore
    this.play
      .updateTrackOrder(this.albumId, this.songs)
      .then(() => {
        console.log("Track order updated successfully!");
        // Optionally show a success message to the user
      })
      .catch((err) => {
        console.error("Error updating track order:", err);
        // Optionally show an error message and revert the local change
      });
  }

  get tracks(): UntypedFormArray {
    return this.albumForm.get("tracks") as UntypedFormArray;
  }

  addTrack() {
    const track = this.fb.group({
      title: "",
      url: "",
      lyrics: "",
      artists: "",
      comments: [],
      likes: [],
      apple: "",
      uri: "",
    });
    this.tracks.push(track);
  }

  async onArtSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files[0];
    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        this.imagePreview = reader.result; // This is the preview URL
      };

      reader.readAsDataURL(file);
      console.log(file);
      this.picBlob = file;

      // Use the file as a Blob for uploads or further processing
      // file is already a Blob
    }
  }

  onTrackSelected(event: Event, index: number) {
    const file = (event.target as HTMLInputElement).files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith(".mp3")) {
        alert("Please upload only .mp3 files for music.");
        (event.target as HTMLInputElement).value = ""; // Reset the file input
        return;
      }
      this.tracksFiles = file;
      const audioElement = document.createElement("audio");
      audioElement.preload = "metadata";
      audioElement.onloadedmetadata = () => {
        window.URL.revokeObjectURL(audioElement.src);
        const durationInSeconds = audioElement.duration;
        const minutes = Math.floor(durationInSeconds / 60);
        const seconds = Math.floor(durationInSeconds % 60);
        const formattedDuration = `${minutes}:${seconds
          .toString()
          .padStart(2, "0")}`;
        this.trackForm.patchValue({ duration: formattedDuration });
      };
      audioElement.src = URL.createObjectURL(file);
    }
  }

  uploadArt() {}

  async uploadAlbum() {
    const albumData = { ...this.albumForm.value };
    albumData.price = parseFloat(albumData.price) || 0;
    if (!albumData.membersOnly) {
      albumData.tier = "";
      albumData.level = "";
    }
    delete albumData.tracks; // Tracks are a subcollection

    if (this.editMode) {
      await this.play.updateAlbum(this.albumId, albumData);
      if (this.picBlob) {
        const formData = new FormData();
        formData.append("file", this.picBlob, this.picBlob.name);
        formData.append("uploadType", "artwork");
        formData.append("creatorId", this.creatorId);
        formData.append("albumId", this.albumId);

        this.http.post(`${environment.myBackend}/upload`, formData).subscribe({
          next: (res: any) => {
            this.play.updateAlbum(this.albumId, { art: res.url }).then(() => {
              this.router.navigate(["home"]);
            });
          },
          error: (err) => this.router.navigate(["home"]), // Navigate even if art fails
        });
      } else {
        this.router.navigate(["home"]);
      }
    } else {
      // Add artistId to the album data for creation
      if (this.isLabelAccount) {
        const selectedArtist = this.currentUser.labelArtists.find(
          (a) => a.name === albumData.artist
        );
        if (selectedArtist) {
          albumData.artistId = selectedArtist.id;
        } else {
          console.error("Could not find artist ID for selected label artist.");
          // TODO: Show an error to the user
          return;
        }
      } else {
        albumData.artistId = this.currentUser.id;
      }

      // 1. Create the album document in Firestore first
      await this.play.addDisco(albumData, this.albumId);

      // 2. If there's artwork, upload it to your server
      if (this.picBlob) {
        const formData = new FormData();
        formData.append("file", this.picBlob, this.picBlob.name);
        formData.append("uploadType", "artwork");
        formData.append("creatorId", this.creatorId);
        formData.append("albumId", this.albumId);

        this.http.post(`${environment.myBackend}/upload`, formData).subscribe({
          next: (res: any) => {
            // 3. Update the album document with the artwork URL
            this.play.updateAlbum(this.albumId, { art: res.url }).then(() => {
              this.router.navigate(["home"]);
            });
          },
          error: (err) => {
            console.error("Error uploading artwork:", err);
            this.router.navigate(["home"]);
          },
        });
      } else {
        this.router.navigate(["home"]);
      }
    }
  }

  editTrack(i, track) {
    this.trackIndex = i;
    this.showTrackForm = true; // Ensure form is visible

    // Patch the main form values first (title, lyrics, etc.)
    this.trackForm.patchValue(track);

    // Then, manually clear and rebuild the artists FormArray
    const artistsArray = this.trackForm.get("artists") as UntypedFormArray;
    artistsArray.clear();
    if (track.artists && Array.isArray(track.artists)) {
      track.artists.forEach((artist) => {
        artistsArray.push(this.fb.group(artist));
      });
    }
  }

  async onVidSubmit() {
    this.isUploadingVideo = true;
    const videoFile = this.uploadVidForm.get("video").value;
    const thumbnailFile = this.uploadVidForm.get("thumbnail").value;
    const videoDataFromForm = this.uploadVidForm.getRawValue();
    if (!videoDataFromForm.membersOnly) {
      videoDataFromForm.tier = "";
      videoDataFromForm.level = "";
    }

    if (!videoFile || !(videoFile instanceof File)) {
      console.error("Invalid video file selected");
      return;
    }
    const videoId = this.play.generateId("video");
    const videoData = {
      id: videoId,
      title: videoDataFromForm.title,
      description: videoDataFromForm.description,
      youtubeLink: videoDataFromForm.youtubeLink,
      creator: { id: this.creatorId, name: this.currentUser.displayName },
      created: new Date().toISOString(),
      duration: Math.round(videoDataFromForm.duration),
      releaseDate: videoDataFromForm.releaseDate,
      dspDate: videoDataFromForm.dspReleaseDate,
      type: videoDataFromForm.type,
      showing: videoDataFromForm.showing,
      views: 0,
      price: parseFloat(videoDataFromForm.price) || 0,
      membersOnly: videoDataFromForm.membersOnly,
      tier: videoDataFromForm.tier,
      level: videoDataFromForm.level,
    };
    await this.play.addVideo(videoData, videoId);
    const formData = new FormData();
    formData.append("file", videoFile, videoFile.name);
    if (thumbnailFile) {
      formData.append("thumbnail", thumbnailFile, "thumbnail.jpg");
    }
    formData.append("uploadType", "video");
    formData.append("creatorId", this.creatorId);
    formData.append("videoId", videoId);

    // 3. Upload to your server and update Firestore doc on success
    this.http
      .post(`${environment.myBackend}/upload-video`, formData, {
        reportProgress: true,
        observe: "events",
      })
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadProgress = Math.round(
              (event.loaded / event.total) * 100
            );
            console.log(`Upload progress: ${this.uploadProgress}%`);
          } else if (event.type === HttpEventType.Response) {
            const res: any = event.body;
            console.log("Video uploaded, server response:", res);
            // Update video doc with URL and thumbnail from server
            const updateData = { link: res.url, thumbnail: res.thumbnail };
            this.play.updateVideo(videoId, updateData);
            this.uploadProgress = 0;
            this.uploadVidForm.reset();
            this.thumbnailPreview = "";
          }
        },
        error: (err) => {
          console.error("Error uploading video to server:", err);
          this.uploadProgress = 0;
        },
      });
  }

  onThumbnailSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files[0];
    if (file) {
      this.uploadVidForm.patchValue({ thumbnail: file });

      const reader = new FileReader();
      reader.onload = () => {
        this.thumbnailPreview = reader.result;
      };
      reader.readAsDataURL(file);
      console.log("Custom thumbnail selected.");
    }
  }

  onFileChange(event: any) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      this.uploadVidForm.patchValue({ video: file });
      this.thumbnailPreview = ""; // Reset preview
      console.log("File selected:", file.name);

      const videoElement = document.createElement("video");
      videoElement.preload = "metadata";

      const videoUrl = URL.createObjectURL(file);

      videoElement.onloadedmetadata = () => {
        this.uploadVidForm.patchValue({ duration: videoElement.duration });
        videoElement.currentTime = 1; // Seek to 1 second
      };

      videoElement.onseeked = () => {
        const canvas = document.createElement("canvas");
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

          // Create a blob from the canvas
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Convert the blob to a File object to prevent WebKitBlobResource errors in Safari.
                const thumbnailFile = new File([blob], "thumbnail.jpg", {
                  type: "image/jpeg",
                });
                this.uploadVidForm.patchValue({ thumbnail: thumbnailFile });

                const reader = new FileReader();
                reader.onload = () => {
                  this.thumbnailPreview = reader.result;
                };
                // Read the original blob for the preview
                reader.readAsDataURL(blob);
                console.log("Thumbnail generated.");
              }
              URL.revokeObjectURL(videoUrl); // Clean up the object URL
            },
            "image/jpeg",
            0.9
          ); // quality
        } else {
          console.error("Could not get canvas context.");
          URL.revokeObjectURL(videoUrl); // Clean up
        }
      };

      videoElement.onerror = () => {
        console.error("Error loading video for thumbnail generation.");
        URL.revokeObjectURL(videoUrl); // Clean up on error
      };

      videoElement.src = videoUrl;
    }
  }

  navigateToUpgrade() {
    // TODO: Implement navigation to your subscription/upgrade page
    this.router.navigate(["/pricing"]); // Example route
  }

  backToSelection() {
    this.selection = null;
    this.showUpgradePrompt = false;
  }

  cancel() {
    if (this.selection && !this.editMode) {
      this.backToSelection();
    } else {
      this.router.navigate(["/home"]);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
